import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AuthState, GroupSettings, TeacherGroup, UserAvatar, UserState } from '@/types/user';
import {
  LanguageSettingsResponse,
  LevelSettingsResponse,
  MeResponse,
  ResponseMissingGooglePermissions,
  LevelTestResultResponse,
  ImprovableRecentActivitiesObject,
  AchievementOfTheWeekResponse,
} from '@/types/response';
import { Progress, TeacherSettings } from '@/types/user';
import { RootState } from '@/types/common';
import userThunks from '../thunks/user';
import { SkillsProgress } from '@/types/progress';
import { EditGroupDetails } from '@/types/register';
import ReactGA from 'react-ga4';
import { EventNames } from '@/types/analytics';

const defaultAuthState: AuthState = {
  appLoaded: false,
  loggedIn: false,
  user: null,
  userRole: localStorage.getItem('userRole') ?? 'guest',
  userProgress: null,
  skillSettings: [],
  showWelcomeScreen: false,
  isNewlyRegistered: false,
  showAppRecommandationScreen: false,
  showAvatarSelection: false,
  isFromRegistration: false,
  displayDictionary: false,
  shouldStartFinalAssessment: false,
  token: localStorage.getItem('token'),
  userId: localStorage.getItem('userId'),
  loggedUserId: localStorage.getItem('loggedUserId'),
  nativeLanguage: localStorage.getItem('native-language') ?? 'pt',
  studyingLanguage: localStorage.getItem('studying-language') ?? 'en',
  interfaceLanguage: localStorage.getItem('interface-language'),
  levels: [],
  languages: [],
  improvableRecentQuizzes: [],
  comingUpQuizzes: [],
  improvableRecentActivities: [],
  achievementOfTheWeek: null,
  missingGooglePermissions: null,
  getUserInfoError: '',
  placementTestCompleted: localStorage.getItem('placementTestCompleted') !== 'false',
  dealExpiredRole: null,
};

const auth = createSlice({
  name: 'Auth',
  initialState: defaultAuthState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('token', action.payload);
    },
    setLoggedUserId: (state, action: PayloadAction<string>) => {
      const userId = action.payload;

      if (userId) {
        state.userId = userId;
        state.loggedUserId = userId;
        localStorage.setItem('userId', userId);
        localStorage.setItem('loggedUserId', userId);
      } else {
        state.loggedIn = false;
        state.user = null;
        state.userRole = 'guest';
        state.userId = null;
        state.loggedUserId = null;
        state.token = null;
        state.userProgress = null;
        state.placementTestCompleted = true;

        localStorage.clear();

        localStorage.setItem('userRole', 'guest');

        state.interfaceLanguage = 'en';
        localStorage.setItem('interface-language', 'en');
      }
    },
    setDictionaryDisplay: (state, action: PayloadAction<boolean>) => {
      state.displayDictionary = action.payload;
    },
    setNativeLanguage: (state, action: PayloadAction<string>) => {
      state.nativeLanguage = action.payload;
      localStorage.setItem('native-language', action.payload);
    },
    setCognitoRole: (state, action: PayloadAction<string>) => {
      state.userRole = action.payload.toLowerCase();
      localStorage.setItem('userRole', action.payload.toLowerCase());
    },
    setInterfaceLanguage: (state, action: PayloadAction<string>) => {
      state.interfaceLanguage = action.payload;
      localStorage.setItem('interface-language', action.payload);

      if (state.user) {
        const user = state.user as UserState;
        if (user?.metadata?.languagePreferences) {
          state.user = {
            ...user,
            metadata: {
              ...user.metadata,
              languagePreferences: {
                ...user.metadata.languagePreferences,
                interfaceLanguage: action.payload,
              },
            },
          };
        }
      }
    },
    setStudyingLanguage: (state, action: PayloadAction<string>) => {
      state.studyingLanguage = action.payload;
      localStorage.setItem('studying-language', action.payload);
    },
    signOut: state => {
      /// Track hint used event
      ReactGA.event(EventNames.Logout);

      state.loggedIn = false;
      state.user = null;
      state.userRole = 'guest';
      state.userId = null;
      state.loggedUserId = null;
      state.token = null;
      state.userProgress = null;
      state.placementTestCompleted = true;

      localStorage.clear();
      sessionStorage.clear();

      localStorage.setItem('userRole', 'guest');
      // Keeping the last language after the logout - ELL-4248
      // state.interfaceLanguage = 'en';
      // localStorage.setItem('interface-language', 'en');
    },
    refreshUserProgress: state => {
      state.userProgress = null;
    },
    signIn: (state, action: PayloadAction<{ token: string; userId: string }>) => {
      /// Track hint used event
      ReactGA.event(EventNames.Login);

      const { token, userId } = action.payload;

      (window as any).userDataIsLogIn = true;

      state.token = token;
      state.userId = userId;
      state.loggedUserId = userId;
      state.showWelcomeScreen = true;
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      localStorage.setItem('loggedUserId', userId);
    },
    setLogin: (state, action: PayloadAction<boolean>) => {
      state.loggedIn = action.payload;
    },
    setUserProgress: (state, action: PayloadAction<{ userProgress: SkillsProgress }>) => {
      state.userProgress = action.payload.userProgress;
    },
    setProgress: (state, action: PayloadAction<Progress>) => {
      if (state.user) {
        // state.user.progress = action.payload;
        // ELL-5984 Preserve userStreakResponses fallback if it's not in the payload
        const existingStreak = state.user.progress?.userStreakResponses;
        const newProgress = action.payload;

        state.user.progress = {
          ...newProgress,
          userStreakResponses: newProgress.userStreakResponses ?? existingStreak,
        };
      }
    },

    setPlacementTestCompleted: (state, action: PayloadAction<boolean>) => {
      state.placementTestCompleted = action.payload;
      localStorage.setItem('placementTestCompleted', String(action.payload));
    },

    presetBypassedLevel: (state, action: PayloadAction<{ userLevel: number }>) => {
      if (state.user) {
        const existingProgress = state.user.progress;
        state.user.progress = {
          userId: existingProgress?.userId ?? state.user.id,
          userLevel: action.payload.userLevel,
          coins: existingProgress?.coins ?? 0,
          completedUnits: existingProgress?.completedUnits ?? 0,
          completedTopics: existingProgress?.completedTopics ?? 0,
          completedActivities: existingProgress?.completedActivities ?? 0,
          achievedStars: existingProgress?.achievedStars ?? 0,
          unit: existingProgress?.unit ?? { topicsOverviewPerUnit: {} },
          topic: existingProgress?.topic ?? { activitiesOverviewPerTopic: {} },
          userStreakResponses: existingProgress?.userStreakResponses as any,
        };
      }
      state.placementTestCompleted = false;
      localStorage.setItem('placementTestCompleted', 'false');
    },
    setUserCanDoAnswer: (
      state,
      action: PayloadAction<{ unitId: number; topicId: number; answerId: number }>
    ) => {
      const { unitId, topicId, answerId } = action.payload;

      if (state?.user?.progress?.unit?.topicsOverviewPerUnit?.[unitId]?.[topicId]) {
        if (!state.user.progress.unit.topicsOverviewPerUnit[unitId][topicId].canDo) {
          state.user.progress.unit.topicsOverviewPerUnit[unitId][topicId].canDo = {};
        }

        state.user.progress.unit.topicsOverviewPerUnit[unitId][topicId].canDo.userAnswered =
          answerId;
      }
    },
    appLoaded: state => {
      state.appLoaded = true;
    },
    setUserAfterLevelTest: (state, action: PayloadAction<LevelTestResultResponse>) => {
      if (state.user !== null) {
        // state.user.achievements.coins = action.payload.userLevel?.startingReward ?? 0;
        // state.user.level = action.payload.userLevel?.userGeneralLevel;
        // state.showAvatarSelection = state.user?.metadata.userSettings.avatar ? false : true;
        // state.user.currentLevelByskill = action.payload.userLevel?.currentLevelByskill;

        /// Track level test completed event
        const levelEventMapping: { [key: number]: string } = {
          1: EventNames.Leveltest_result_1,
          2: EventNames.Leveltest_result_2,
          3: EventNames.Leveltest_result_3,
          4: EventNames.Leveltest_result_4,
          5: EventNames.Leveltest_result_5,
        };

        ReactGA.event(levelEventMapping[action.payload.userLevel?.userGeneralLevel ?? 1]);
      }
    },

    setShowWelcomeScreen: (state, action: PayloadAction<boolean>) => {
      state.showWelcomeScreen = action.payload;
    },
    setShowAppRecommandationScreen: (state, action: PayloadAction<boolean>) => {
      state.showAppRecommandationScreen = action.payload;
    },
    setIsNewlyRegistered: (state, action: PayloadAction<boolean>) => {
      state.isNewlyRegistered = action.payload;
    },
    setShowAvatarSelection: (state, action: PayloadAction<boolean>) => {
      state.showAvatarSelection = action.payload;
    },
    setAvatar: (state, action: PayloadAction<UserAvatar>) => {
      if (state.user) {
        state.user.metadata.userSettings.avatar = action.payload;
      }
    },
    setUserCoins: (state, action: PayloadAction<number>) => {
      if (state.user && state.user.progress) {
        state.user.progress.coins = action.payload;
      }
    },
    /**
     * Refreshes the cached groupSettings (e.g. allowIndividualAdvancement) for the
     * logged-in user. Session data is fetched once at login/app-boot and never
     * refetched, so if a teacher changes class-level settings mid-session, students
     * already logged in only see the update via an explicit refresh like this one.
     */
    setGroupSettings: (state, action: PayloadAction<GroupSettings | undefined>) => {
      if (state.user?.metadata?.organizationDetails) {
        state.user.metadata.organizationDetails.groupSettings = action.payload;
      }
    },
    setIsFromRegistration: (state, action: PayloadAction<boolean>) => {
      state.isFromRegistration = action.payload;
    },
    addNewClass: (state, action: PayloadAction<TeacherGroup>) => {
      if (state.user?.metadata?.organizationDetails?.teacher?.teacherGroups) {
        const teacherGroups = state.user?.metadata?.organizationDetails?.teacher?.teacherGroups;
        teacherGroups?.push(action.payload);
      }
    },
    removeClass: (state, action: PayloadAction<string>) => {
      const teacher = state.user?.metadata?.organizationDetails?.teacher;
      if (teacher?.teacherGroups) {
        teacher.teacherGroups = teacher.teacherGroups.filter(group => group.id !== action.payload);
        teacher.totalStudents = teacher.teacherGroups.reduce(
          (sum, group) => sum + (group.totalStudents ?? 0),
          0
        );
      }
    },
    updateTeacherClass: (state, action: PayloadAction<EditGroupDetails>) => {
      if (state.user?.metadata?.organizationDetails?.teacher?.teacherGroups) {
        const { groupId: id, grade, groupName: name } = action.payload;
        const teacherGroups = state.user?.metadata?.organizationDetails?.teacher?.teacherGroups;
        const groupIndex = teacherGroups.findIndex(i => i.id === id);
        if (Number.isInteger(groupIndex)) {
          teacherGroups[groupIndex].name = name;
          teacherGroups[groupIndex].grade = grade;
        }
      }
    },
    setShouldStartFinalAssessment: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.shouldStartFinalAssessment = action.payload;
      }
    },
    updatedClassroomStudentsAmount: (
      state,
      action: PayloadAction<{ id: string; amount: number }>
    ) => {
      const { id, amount } = action.payload;

      if (state.user?.metadata?.organizationDetails?.teacher?.teacherGroups) {
        const teacherGroups = state.user?.metadata?.organizationDetails?.teacher?.teacherGroups;
        let totalStudents = 0;
        teacherGroups.forEach(group => {
          if (group.id === id) {
            group.totalStudents = (group.totalStudents ?? 0) + amount;
            if (group.totalStudents < 0) {
              group.totalStudents = 0;
            }
          }
          totalStudents += group.totalStudents ?? 0;
        });
        state.user.metadata.organizationDetails.teacher.totalStudents = totalStudents;
      }
    },
    clearMissingGooglePermissions: state => {
      state.missingGooglePermissions = null;
      state.getUserInfoError = '';
    },
    updateTeacherGroupsData: (state, action: PayloadAction<TeacherSettings>) => {
      const teacherSettings = action.payload;
      if (state.user?.metadata?.organizationDetails?.teacher) {
        state.user.metadata.organizationDetails.teacher.teacherGroups =
          teacherSettings.teacherGroups;
        state.user.metadata.organizationDetails.teacher.totalStudents =
          teacherSettings.totalStudents;
      }
    },
  },
  extraReducers: builder => {
    // USER INFO EXTRA REDUCERS
    builder.addCase(userThunks.userInfo.fulfilled, (state, action) => {
      const payload = action.payload as unknown as MeResponse;
      const user = {
        ...payload.data,
      };

      const userId = (user as UserState).metadata?.id?.toString() || state.userId?.toString();
      state.loggedIn = true;
      state.user = { ...(user as UserState), id: userId ?? '' };
      state.userId = userId ?? '';

      const userRole = (user as UserState)?.metadata?.role?.toLowerCase() || 'guest';
      state.userRole = userRole;
      localStorage.setItem('userRole', userRole);

      if (state.user?.metadata?.languagePreferences?.interfaceLanguage) {
        state.interfaceLanguage = state.user.metadata.languagePreferences.interfaceLanguage;
        localStorage.setItem('interface-language', state.interfaceLanguage);
      }

      if (state.user?.metadata?.languagePreferences?.nativeLanguage) {
        state.nativeLanguage = state.user.metadata.languagePreferences.nativeLanguage;
        localStorage.setItem('native-language', state.nativeLanguage);
      }

      state.missingGooglePermissions = null;
      state.getUserInfoError = '';
      state.dealExpiredRole = null;
    });

    builder.addCase(userThunks.fetchLevels.fulfilled, (state, action) => {
      const payload = action.payload as unknown as LevelSettingsResponse;
      state.levels = payload.data;
    });

    builder.addCase(userThunks.fetchLanguages.fulfilled, (state, action) => {
      const payload = action.payload as unknown as LanguageSettingsResponse;
      state.languages = payload.data;
    });

    builder.addCase(userThunks.userInfo.rejected, (state, action) => {
      const payload = action.payload as any;
      const roleBeforeClear = state.userRole;

      state.user = null;
      state.userRole = 'guest';
      state.userId = null;
      state.loggedUserId = null;
      state.token = null;
      state.loggedIn = false;
      state.appLoaded = true;

      let getUserInfoError: '' | 'GooglePermissions' | 'DealHasExpired' = '';
      const responseCode = payload?.error?.response?.status || 0;
      const responseStatus = (payload?.error?.response?.data?.status || '').toLowerCase();
      const responseMessage = (payload?.error?.response?.data?.message || '').toLowerCase();

      if (payload?.missingPermissions && payload.missingPermissions.length > 0) {
        state.missingGooglePermissions = payload.missingPermissions;
        getUserInfoError = 'GooglePermissions';
      } else if (
        (responseCode === 401 || responseCode === 403) &&
        (responseMessage.includes('this deal has expired') ||
          responseMessage.includes('user deal has expired'))
      ) {
        getUserInfoError = 'DealHasExpired';
        state.dealExpiredRole = roleBeforeClear;
      }

      state.getUserInfoError = getUserInfoError;

      localStorage.clear();

      localStorage.setItem('userRole', 'guest');
    });

    builder.addCase(userThunks.fetchImprovableRecentActivities.fulfilled, (state, action) => {
      const payload = action.payload as unknown as ImprovableRecentActivitiesObject;
      state.improvableRecentActivities = payload.data.data;
    });

    builder.addCase(userThunks.fetchImprovableRecentActivities.rejected, state => {
      state.improvableRecentActivities = [];
    });

    builder.addCase(userThunks.fetchAchievementOfTheWeek.fulfilled, (state, action) => {
      const payload = action.payload as unknown as AchievementOfTheWeekResponse;
      state.achievementOfTheWeek = payload.data;
    });

    builder.addCase(userThunks.fetchAchievementOfTheWeek.rejected, state => {
      state.achievementOfTheWeek = null;
    });
  },
});

export const authActions = auth.actions;

export const authSelector = (state: RootState) => state.auth;
export const skillsSelector = (state: RootState) => state.auth.skillSettings;
export const isLangLocalSelector = (state: RootState) => {
  const { interfaceLanguage, nativeLanguage } = state.auth;

  return interfaceLanguage === nativeLanguage;
};

export const interfaceLanguageSelector = (state: RootState) => {
  const { languages, interfaceLanguage, studyingLanguage } = state.auth;

  const language = languages.find(lang => lang.learningCode === studyingLanguage);

  if (!language) {
    return;
  }

  const interfaceLang = language.localLanguages.find(lang => lang.code === interfaceLanguage);

  return interfaceLang;
};

export const nativeLanguageSelector = (state: RootState) => {
  const { languages, nativeLanguage, studyingLanguage } = state.auth;

  const language = languages.find(lang => lang.learningCode === studyingLanguage);

  if (!language) {
    return;
  }

  const nativeLang = language.localLanguages.find(lang => lang.code === nativeLanguage);

  return nativeLang;
};

export const studyingLanguageSelector = (state: RootState) => {
  const { languages, studyingLanguage } = state.auth;

  const language = languages.find(lang => lang.learningCode === studyingLanguage);

  return language;
};

export default auth.reducer;
